import {
    ActionMenu,
    ActionMenuItem,
    ActionMenuItemGroup,
} from '@thoughtspot/radiant-react/widgets/action-menu';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styles from './more-options.module.scss';

export interface MoreOptionsProps {
    menuId: number;
    menuLabel: string;
    menuItems?: ActionMenuItem[];
    menuItemGroups?: ActionMenuItemGroup[];
    isDisabled?: boolean;
}

export const MoreOptions: React.FC<MoreOptionsProps> = ({
    menuId,
    menuLabel,
    menuItems = [],
    menuItemGroups = [],
    isDisabled = false,
}: MoreOptionsProps) => {
    const [isActionMenuVisible, setIsActionMenuVisible] = useState(false);

    // Hide action menu when any of the menu items is clicked
    const onItemClick = (callBack?: () => void) => {
        setIsActionMenuVisible(false);
        if (callBack) {
            callBack();
        }
    };

    const toggleActionMenu = () => {
        setIsActionMenuVisible(prevState => !prevState);
    };

    /** parsing the action and re-assign to make sure that the pop-up is closed
     * if the action menu item is clicked
     */
    const parseActionItems = (items = menuItems) => {
        const menuItemsParsed = items.map((item: any) => {
            const modifiedItem = {
                ...item,
                onClick: () => {
                    onItemClick(item.onClick);
                },
            };
            return modifiedItem;
        });
        return menuItemsParsed;
    };

    const [parseMenuItems, setParsedMenuItems] = useState<any[]>(
        parseActionItems(),
    );

    const popoverRef = useRef<HTMLDivElement>(null);
    const popperRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleDocumentKeyPress = (event: KeyboardEvent) => {
            if (isActionMenuVisible && event.key === 'Escape') {
                setIsActionMenuVisible(false);
                buttonRef.current?.focus();
            }
        };

        document.addEventListener('keydown', handleDocumentKeyPress);

        return () => {
            document.removeEventListener('keydown', handleDocumentKeyPress);
        };
    }, [isActionMenuVisible]);

    let internalMenuItemGroups: ActionMenuItemGroup[] = [
        {
            title: menuLabel,
            items: parseMenuItems,
        },
    ];

    if (!_.isEmpty(menuItemGroups)) {
        internalMenuItemGroups = menuItemGroups.map(
            (group: ActionMenuItemGroup) => ({
                ...group,
                items: parseActionItems(group.items),
            }),
        );
    }

    const getActionMenu = () => (
        <ActionMenu
            show={isActionMenuVisible}
            menuItemGroups={internalMenuItemGroups}
            data-testid={`options_actionmenu${menuId}`}
        />
    );

    return (
        <>
            <div
                data-tour-id="folder-management-tour"
                data-testid="folder-management-tour"
            >
                <Button
                    onClick={toggleActionMenu}
                    icon={IconID.MORE}
                    type="secondary"
                    ariaLabel={menuLabel}
                    data-tooltip-content={menuLabel}
                    data-testid={`options_button${menuId}`}
                    isDisabled={isDisabled}
                    buttonRef={buttonRef}
                    className={styles.moreButton}
                />
            </div>
            <Popover
                targetRef={buttonRef}
                align="bottom-end"
                isVisible={isActionMenuVisible}
                popperRef={popperRef}
                content={getActionMenu()}
                className={styles.popoverContainer}
                onClickOutside={() => {
                    setIsActionMenuVisible(false);
                }}
            />
        </>
    );
};
